import { Controller } from 'stimulus';

// Collapses a long announcement body behind a fade-out gradient and a "Ler mais"
// toggle. Measures the rendered body: if it fits within the max height, no clamp
// is applied and the controls stay hidden. Only used on the mural (never in email).
// Toggling animates the body height (see the transition classes on the content target).
export default class extends Controller {
  static targets = ['content', 'fade', 'button', 'label', 'icon'];
  static values = { max: { type: Number, default: 280 } };

  connect() {
    // Wait a frame so images/fonts contribute to the measured height.
    requestAnimationFrame(() => this.measure());
  }

  measure() {
    // Small tolerance so bodies that barely exceed the limit are not clamped.
    if (this.contentTarget.scrollHeight <= this.maxValue + 32) return;
    this.clampInstantly();
    this.fadeTarget.classList.remove('hidden');
    this.buttonTarget.classList.remove('hidden');
    this.expanded = false;
  }

  toggle() {
    this.expanded ? this.collapse() : this.expand();
  }

  expand() {
    this.expanded = true;
    this.fadeTarget.classList.add('hidden');
    this.labelTarget.textContent = 'Ver menos';
    this.iconTarget.classList.add('rotate-90');
    // Animate up to the real height, then release the cap so the body reflows freely.
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`;
    this.afterTransition(() => {
      if (this.expanded) this.contentTarget.style.maxHeight = 'none';
    });
  }

  collapse() {
    this.expanded = false;
    this.labelTarget.textContent = 'Ler mais';
    this.iconTarget.classList.remove('rotate-90');
    // Pin to the current pixel height first so the transition has a start value.
    this.contentTarget.style.maxHeight = `${this.contentTarget.scrollHeight}px`;
    requestAnimationFrame(() => {
      this.contentTarget.style.maxHeight = `${this.maxValue}px`;
      this.fadeTarget.classList.remove('hidden');
    });
  }

  // Applies the initial clamp without animating it on page load.
  clampInstantly() {
    const content = this.contentTarget;
    content.style.transition = 'none';
    content.style.maxHeight = `${this.maxValue}px`;
    content.offsetHeight; // force reflow so the value sticks before transitions return
    content.style.transition = '';
  }

  afterTransition(callback) {
    const handler = (event) => {
      if (event.target !== this.contentTarget || event.propertyName !== 'max-height') return;
      this.contentTarget.removeEventListener('transitionend', handler);
      callback();
    };
    this.contentTarget.addEventListener('transitionend', handler);
  }
}
